import { inject, Injectable, signal } from '@angular/core';
import { PlayExecutionReportingService } from '@r1-compass-library/services/reporting/play-execution-reporting.service';
import { PlayExecutionTableService } from '@r1-compass-library/services/reporting/play-execution-table.service';
import { PlayExecutionTableRecord } from '@r1-compass-library/components/reporting/report-table/play-execution-table-record.model';
import {
    PlayExecutionInteractionErrorTableData,
    PlayExecutionInteractionErrorTableService,
} from '@r1-compass-library/services/reporting/play-execution-interaction-error-table.service';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { AggregateResponse } from '@r1-compass-library/models';
import { GroupedCountsService } from '../../grouped-counts.service';
import { PlayResultService } from '@r1-compass-library/services/reporting/play-result.service';
import { DateTime } from 'luxon';

/**
 * Service that retrieves and formats data for the action-activity dialog reporting component
 */
@Injectable({
    providedIn: 'root',
})
export class ActionActivityDialogService {
    readonly #playExecutionReportingService = inject(PlayExecutionReportingService);
    readonly #playResultService = inject(PlayResultService);
    readonly #playExecutionTableService = inject(PlayExecutionTableService);
    readonly #groupedCountsService = inject(GroupedCountsService);
    readonly #playExecutionInteractionErrorTableService = inject(PlayExecutionInteractionErrorTableService);

    reportData = signal<PlayExecutionTableRecord[] | null>(null);
    interactionErrorTableData = signal<PlayExecutionInteractionErrorTableData | null>(null);
    chartData = signal<ActivityChartData[]>([
        { name: 'Created', values: [] },
        { name: 'Updated', values: [] },
        { name: 'Failed', values: [] },
        { name: 'Rejected', values: [] },
    ]);
    appSpecificColumns = this.#playExecutionTableService.appSpecificColumns;
    appSpecificDetails = this.#playExecutionTableService.appSpecificDetails;

    public reset() {
        this.reportData.set(null);
        this.interactionErrorTableData.set(null);
    }

    public getReportingTableData(playId: number, rangeStartIso: string, rangeEndIso: string) {
        this.reset();
        return this.#playExecutionReportingService
            .getExecutionReportingData(playId, rangeStartIso, rangeEndIso)
            .subscribe(data => {
                this.reportData.set(this.#playExecutionTableService.getExecutionTableData(data));
            });
    }

    public getInteractionErrorTableData(playResultId: number) {
        this.interactionErrorTableData.set(null);
        const reportData = this.reportData();
        if (reportData) {
            this.#playExecutionInteractionErrorTableService
                .getInteractionErrorTableData(playResultId, reportData)
                .subscribe(data => this.interactionErrorTableData.set(data));
        }
    }

    public getChartData(filter: string, selectedDivisionIds: number[]) {
        // These requests will get the aggregate counts of num records selected, succeed, failed, reject for the given time range.
        const sumRequests: Observable<AggregateResponse[]>[] = this.generateSumRequests(filter, selectedDivisionIds);

        // If a play result exists, but there are no associated play-result-details, the aggregates calls won't get them.
        // To ensure we report on all play-results, we make these requests to get count of play-results within the time range.
        const playResultCountsRequests = this.buildPlayResultCountsRequests(selectedDivisionIds, filter);

        // From the counts, we can see if the aggregates calls excluded any play-results, and we can account for that before returning the data.

        return forkJoin([...playResultCountsRequests]).pipe(
            // Get counts...
            map(counts => {
                return forkJoin([...sumRequests]).pipe(
                    // Get aggregates...
                    map(aggRes => {
                        // Identify all plays with a play-result in this time range
                        const allPlayIds = new Set(counts.flatMap(el => el).map(el => (el.key = String(el.key))));

                        // Update the aggregate results with any missing play ids.
                        return aggRes.map(res => {
                            const aggPlayIds = new Set(res.map(agg => String(agg.key)));
                            const diff = allPlayIds.difference(aggPlayIds);
                            diff.forEach(id => res.push({ key: id, aggregate: 0 })); // add the missing id

                            // Also sort the aggregates by play ID (better UX)
                            return res.sort((a, b) => Number(a.key) - Number(b.key));
                        });
                    })
                );
            }),
            switchMap(res => res) // flatten the observables
        );
    }

    private buildPlayResultCountsRequests(divisionIds: number[], filter: string) {
        return divisionIds.map(divId => {
            return this.#playResultService.getCounts(
                {
                    divisionId: divId,
                    executionStartRangeBegin: filter,
                    executionStartRangeEnd: DateTime.now().toISO()!,
                    groupByFields: [
                        {
                            tableName: 'play_result',
                            fieldName: 'id',
                        },
                    ],
                    fields: [],
                },
                'playId'
            );
        });
    }

    /**
     * Build an array of Obseravables, where each value corresponds to aggregate response data for aggregate type
     * ex: num successful creates, num successful updates, num failed, or num rejected
     * @param filter
     * @returns
     */
    private generateSumRequests(filter: string, selectedDivisionIds: number[]): Observable<AggregateResponse[]>[] {
        const requestsArray: Observable<AggregateResponse[]>[] = [];

        // Add requests to the array for each aggregates counts type (successful create/update counts are seperated)
        requestsArray.push(
            this.#groupedCountsService.groupedCounts(
                filter,
                selectedDivisionIds,
                'numRecordsSuccessful',
                'numRecordsSuccessful',
                'create'
            )
        );
        requestsArray.push(
            this.#groupedCountsService.groupedCounts(
                filter,
                selectedDivisionIds,
                'numRecordsSuccessful',
                'numRecordsSuccessful',
                'update'
            )
        );
        requestsArray.push(
            this.#groupedCountsService.groupedCounts(
                filter,
                selectedDivisionIds,
                'numRecordsFailed',
                'numRecordsFailed'
            )
        );
        requestsArray.push(
            this.#groupedCountsService.groupedCounts(
                filter,
                selectedDivisionIds,
                'numRecordsRejected',
                'numRecordsRejected'
            )
        );

        return requestsArray;
    }
}

export type ActivityChartData = {
    name: string;
    values: AggregateResponse[];
};
